import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addEventAllocation,
  addEventCost,
  deleteEventAllocation,
  deleteEventCost,
  EventAllocation,
  EventCost,
  EventSettlement,
  getEventAllocations,
  getEventCosts,
  getEventSettlement,
  rollupEventSettlement,
} from "@/api/eventProfitability";

export function useEventSettlement(eventId: string | null | undefined) {
  const [costs, setCosts] = useState<EventCost[]>([]);
  const [allocations, setAllocations] = useState<EventAllocation[]>([]);
  const [settlement, setSettlement] = useState<EventSettlement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingCost, setSavingCost] = useState(false);
  const [savingAllocation, setSavingAllocation] = useState(false);
  const [rollingUp, setRollingUp] = useState(false);
  const [deletingCostId, setDeletingCostId] = useState<string | null>(null);
  const [deletingAllocationId, setDeletingAllocationId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!eventId) {
      setCosts([]);
      setAllocations([]);
      setSettlement(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [costPayload, allocationPayload] = await Promise.all([
        getEventCosts(eventId),
        getEventAllocations(eventId),
      ]);
      setCosts(costPayload ?? []);
      setAllocations(allocationPayload ?? []);
      try {
        const existing = await getEventSettlement(eventId);
        setSettlement(existing);
      } catch {
        setSettlement(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load settlement data");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const createCost = useCallback(
    async (payload: Parameters<typeof addEventCost>[1]) => {
      if (!eventId) return;
      setSavingCost(true);
      try {
        await addEventCost(eventId, payload);
        await load();
      } finally {
        setSavingCost(false);
      }
    },
    [eventId, load],
  );

  const createAllocation = useCallback(
    async (payload: Parameters<typeof addEventAllocation>[1]) => {
      if (!eventId) return;
      setSavingAllocation(true);
      try {
        await addEventAllocation(eventId, payload);
        await load();
      } finally {
        setSavingAllocation(false);
      }
    },
    [eventId, load],
  );

  const rollup = useCallback(async () => {
    if (!eventId) return;
    setRollingUp(true);
    try {
      const updated = await rollupEventSettlement(eventId);
      setSettlement(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to compute settlement");
    } finally {
      setRollingUp(false);
    }
  }, [eventId]);

  const removeCost = useCallback(
    async (costId: string) => {
      if (!eventId) return;
      setDeletingCostId(costId);
      try {
        await deleteEventCost(eventId, costId);
        await load();
      } finally {
        setDeletingCostId(null);
      }
    },
    [eventId, load],
  );

  const removeAllocation = useCallback(
    async (allocationId: string) => {
      if (!eventId) return;
      setDeletingAllocationId(allocationId);
      try {
        await deleteEventAllocation(eventId, allocationId);
        await load();
      } finally {
        setDeletingAllocationId(null);
      }
    },
    [eventId, load],
  );

  return useMemo(
    () => ({
      costs,
      allocations,
      settlement,
      loading,
      error,
      savingCost,
      savingAllocation,
      rollingUp,
      deletingCostId,
      deletingAllocationId,
      createCost,
      createAllocation,
      removeCost,
      removeAllocation,
      rollup,
      refresh: load,
    }),
    [
      costs,
      allocations,
      settlement,
      loading,
      error,
      savingCost,
      savingAllocation,
      rollingUp,
      deletingCostId,
      deletingAllocationId,
      createCost,
      createAllocation,
      removeCost,
      removeAllocation,
      rollup,
      load,
    ],
  );
}
